import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classnames from 'classnames/bind';
import classNames from 'classnames';
import { history, useParams, useIntl } from 'umi';
import { inject, observer } from 'mobx-react';
import { useBoolean, useDebounceEffect, useMemoizedFn } from 'ahooks';
import { Modal } from '@/components';
import AudioBtn from './H5/Microphone';
import CameraBtn from './H5/Camera';
import LeaveBtn from './H5/Leave';

import { RootStore } from '@/models';
import Style from './h5.less';
import ChatBtn from './H5/Chat';
import UnReadChatPopover from './H5/Chat/UnReadChatPopover';
import SimultaneousInterpretationBtn from './H5/SimultaneousInterpretation';
import MemberBtn from './H5/Member';
import SpeakerBtn from './PC/Speaker';
import RecordingBtn from './H5/Recording';
import MoreBtn from './H5/More';

const $c = classnames.bind(Style);

interface ControlProps extends Partial<Pick<RootStore, 'chat' | 'meeting' | 'user'>> {
  RTCInstance?: RootStore['rtc'];
  simultaneous?: RootStore['simultaneousInterpretation'];
  chat?: RootStore['chat'];
  meeting?: RootStore['meeting'];
}
interface ParamsStruct {
  meetingId: string;
}

const ControlBar: React.FC<ControlProps> = inject(({ store }) => {
  return {
    meeting: store.meeting,
    RTCInstance: store.rtc,
    chat: store.chat,
    simultaneous: store.simultaneousInterpretation,
  };
})(
  observer((props) => {
    const { meeting, RTCInstance, chat, simultaneous } = props;
    const [isChatPopoverVisible, setIsChatPopoverVisible] = React.useState(false);
    const [moreBtn, setMoreBtn] = useState(false);
    const [showMore, { set: setShowMore, setFalse: closeShowMore }] = useBoolean(false);
    const { meetingId } = useParams<ParamsStruct>();
    const { formatMessage } = useIntl();
    const controlGroupRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
      if (RTCInstance?.rtcTrackEnded) {
        Modal.info({
          title: formatMessage({ id: 'DEVICE_IS_ABNORMAL' }),
          okText: formatMessage({ id: 'REFRESH' }),
          okButtonProps: {
            className: `btn-blue`,
          },
          onOk() {
            history.go(0);
          },
        });
      }
    }, [RTCInstance?.rtcTrackEnded]);

    // 有更多按钮但是没有展开更多时，需要隐藏 order 为 2 的按钮
    const hideOrder2Btn = useMemo(() => {
      if (moreBtn) {
        return !showMore;
      }
      return false;
    }, [moreBtn, showMore]);

    const closeShowMoreClick = useMemoizedFn(() => {
      moreBtn && closeShowMore();
    });

    useDebounceEffect(
      () => {
        // 根据按钮的多少，判断是否展示多排按钮
        if (controlGroupRef.current) {
          const btnLength = controlGroupRef.current.children.length;
          console.log('btnLength', btnLength);
          let num = 5;
          if (moreBtn) {
            num = 6;
          }
          setMoreBtn(btnLength > num);
        }
      },
      [meeting?.ableAppsMap.WebRecording, simultaneous?.interpretationStatus, moreBtn],
      { wait: 1 }
    );

    // 打开聊天面板
    const chatPanelBtnClick = React.useCallback(
      (e, locateMsg = false) => {
        if (RTCInstance?.breakLineReconnecting) {
          return;
        }
        if (!chat?.isAbleUse) return;
        setIsChatPopoverVisible(false);
        e.nativeEvent.stopImmediatePropagation();
        chat?.setChatVisible(!chat?.chatVisible, locateMsg);
        chat?.chatVisible && meeting?.setPeoplePanelShow(false);
        if (moreBtn && showMore) {
          closeShowMoreClick();
        }
      },
      [chat, moreBtn, showMore, RTCInstance, closeShowMoreClick]
    );

    // 在聊天按钮上显示新消息气泡
    const chatBtnPopoverVisible = useMemo(() => {
      if (moreBtn) {
        // 更多按钮显示，根据是否展开更多面板和聊天面板状态决定是否展示
        return showMore && isChatPopoverVisible && !chat?.chatVisible;
      }
      // 没有更多按钮，根据聊天面板状态决定是否展示
      return isChatPopoverVisible && !chat?.chatVisible;
    }, [showMore, moreBtn, chat?.chatVisible, isChatPopoverVisible]);

    // 在更多按钮上显示新消息气泡
    const moreBtnPopoverVisible = useMemo(() => {
      if (moreBtn) {
        // 更多按钮显示，根据聊天面板状态决定是否展示
        return !showMore && isChatPopoverVisible && !chat?.chatVisible;
      }
      // 没有更多按钮，不展示
      return false;
    }, [showMore, moreBtn, chat?.chatVisible, isChatPopoverVisible]);

    return (
      <>
        {showMore && <div className={$c('more-bar-modal')} onClick={closeShowMore} />}
        <div className={$c('control-bar')}>
          <div
            ref={controlGroupRef}
            className={classNames(Style['control-group'], {
              [Style['more-bar']]: moreBtn,
              [Style['hide-more']]: hideOrder2Btn,
            })}
          >
            <AudioBtn />
            <CameraBtn />
            <MemberBtn />
            <RecordingBtn className={Style.order2} order={moreBtn ? 2 : 1} />
            <UnReadChatPopover
              onPopoverVisibleChange={setIsChatPopoverVisible}
              popoverVisible={chatBtnPopoverVisible}
              onClick={(e) => {
                chatPanelBtnClick(e, true);
              }}
            >
              <ChatBtn
                className={Style.order2}
                onClick={(e) => {
                  chatPanelBtnClick(e, false);
                }}
                order={moreBtn ? 2 : 1}
              />
            </UnReadChatPopover>
            <SimultaneousInterpretationBtn className={Style.order2} order={moreBtn ? 2 : 1} />
            <UnReadChatPopover
              onPopoverVisibleChange={setIsChatPopoverVisible}
              popoverVisible={moreBtnPopoverVisible}
              onClick={(e) => {
                chatPanelBtnClick(e, true);
              }}
            >
              <MoreBtn showMore={showMore} onClick={setShowMore} isShow={moreBtn} order={1} />
            </UnReadChatPopover>
            <SpeakerBtn />
            <LeaveBtn meetingId={meetingId} />
          </div>
        </div>
      </>
    );
  })
);

export default ControlBar;
